// components/notifications/InvitationNotificationCard/index.jsx
import React, { useState } from 'react';
import {
  Box, Avatar, Typography, Link as MuiLink, Button, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { GroupAdd as GroupAddIcon, Circle as CircleIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useUser } from 'shared/helpers/axios';
import { useTheme } from '@mui/material/styles';


// Helper to compute initials from a name
const initialsFromName = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0]?.[0] ?? '';
  const second = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return (first + second).toUpperCase();
};

function InvitationNotificationCard({
  notification,
  onMarkRead,
  handleNotificationAction,
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const payload = notification.payload || {};
  const invitationId = payload.consortium_invitation_id;
  const consortiumId = payload.consortium_id;
  const consortiumName = payload.consortium_name || 'Unknown Consortium';

  const { user: currentUser } = useUser();
  const fromUserName = payload.from_user_name || payload.sender_name || 'Someone';
  const fromUserId = payload.from_user_id || payload.sender_id;
  const fromUserAvatarUrl = payload.from_user_avatar_url || payload.sender_avatar_url || null;

  const toUserName = payload.to_user_name || payload.recipient_name || '';
  const toUserAvatarUrl = payload.to_user_avatar_url || null;

  const role = (payload.role || '').toString().toLowerCase();
  const action = (payload.action || payload.invitation_status || '').toString().toLowerCase();
  const isPending = action === 'pending' || action === 'invited' || action === '';
  const isInviter = role !== 'recipient';

  // For pending invitations the actor is the inviter (from_user).
  // When the viewer IS the inviter, use the current user's own avatar so it
  // always shows "You invited …" with the viewer's own face.
  const actorName = isPending
    ? (isInviter ? (currentUser?.name || fromUserName) : fromUserName)
    : (toUserName || fromUserName);
  const actorAvatarUrl = isPending
    ? (isInviter ? (currentUser?.avatar || currentUser?.avatar_url || fromUserAvatarUrl) : fromUserAvatarUrl)
    : (isInviter ? toUserAvatarUrl : (currentUser?.avatar || currentUser?.avatar_url));
  const actorInitials = initialsFromName(actorName);

  const fromUserInitials = actorInitials;

  // console.log("****", payload)

  const [imgBroken, setImgBroken] = useState(false);
  const showInitials = !actorAvatarUrl || imgBroken;

  const isRecipient = role === 'recipient';
  const isAccepted = action === 'accepted';
  const isCancelled = action === 'cancelled';

  const consortiumUrl = consortiumId ? `/consortium/${consortiumId}` : null;
  const senderProfileUrl = fromUserId ? `/profile/${fromUserId}` : null;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const handleMarkRead = () => {
    if (!notification.is_read && onMarkRead) {
      onMarkRead();
    }
  };

  const handleSenderClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleMarkRead();
    if (senderProfileUrl) {
      navigate(senderProfileUrl);
    }
  };

  const handleCardClick = () => {
    handleMarkRead();
    if (consortiumUrl) {
      navigate(consortiumUrl);
    }
  };

  const handleAccept = async (e) => {
    e.stopPropagation();
    setActionLoading(true);
    try {
      await handleNotificationAction(notification.id, { 
        action: 'accept_invitation', 
        meta: { id: invitationId } 
      }, e);
    } catch (error) {
      // Error is already displayed via snackbar in NotificationContext
      console.error('Error accepting invitation:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async (e) => {
    e.stopPropagation();
    setActionLoading(true);
    try {
      await handleNotificationAction(notification.id, { 
        action: 'decline_invitation', 
        meta: { id: invitationId } 
      });
    } catch (error) {
      // Error is already displayed via snackbar in NotificationContext
      console.error('Error declining invitation:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmCancel = async () => {
    setActionLoading(true);
    try {
      const cancelAction = isRecipient ? 'cancel_acceptance' : 'cancel_invitation';
      await handleNotificationAction(notification.id, { 
        action: cancelAction, 
        meta: { id: invitationId } 
      });
    } catch (error) {
      // Error is already displayed via snackbar in NotificationContext
      console.error('Error cancelling invitation:', error);
    } finally {
      setActionLoading(false);
      setConfirmOpen(false);
    }
  };

  // Build the message text
  const getMessage = () => {
    if (isRecipient) {
      if (isPending) {
        const reminded = payload.action === 'reminded';
        return (
          <>
            invited you to join{' '}
            <Box component="span" fontWeight={500} color="text.primary">
              {consortiumName}
            </Box>
            {reminded && (
              <Box component="span" display="block" sx={{ mt: 0.5, fontSize: 13 }}>
                Reminder from {payload.reminded_by || fromUserName}: the invitation is still open
                {payload.reminder_note ? <> · <i>“{payload.reminder_note}”</i></> : null}
              </Box>
            )}
          </>
        );
      }
      if (isAccepted) {
        return (
          <>
            You accepted{' '}
            <Box component="span" fontWeight={500} color="text.primary">
              {fromUserName}
            </Box>
            's invitation to join{' '}
            <Box component="span" fontWeight={500} color="text.primary">
              {consortiumName}
            </Box>
          </>
        );
      }
      if (isCancelled) {
        return (
          <>
            Invitation to{' '}
            <Box component="span" fontWeight={500} color="text.primary">
              {consortiumName}
            </Box>{' '}
            was cancelled
          </>
        );
      }
    } else {
      // invite
      const recipientName = payload.recipient_name || payload.to_user_name || 'User';
      if (isPending) {

        // {console.log(">>>", payload) ;}
        return (
         

          <>
             <Box component="span" color="text.primary" fontWeight={500}>You</Box> invited{' '}
            <Box component="span" color="text.primary" fontWeight={500}>
              {recipientName}
            </Box>{' '}
            to{' '}
            <Box component="span" color="text.primary" fontWeight={500}>
              {consortiumName}
            </Box>
          </>
        );
      }
      if (isAccepted) {
        return (
          <>
            <Box component="span" fontWeight={500} color="text.primary">
              {recipientName}
            </Box>{' '}
            accepted your invitation to{' '}
            <Box component="span" fontWeight={500} color="text.primary">
              {consortiumName}
            </Box>
          </>
        );
      }
    }
    return consortiumName;
  };

  // Render simple notification for non-pending or non-recipient
  if (!isRecipient || !isPending) {
    return (
      <Box
        onClick={handleCardClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1,
          px: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
          transition: 'background-color 0.2s',
          cursor: consortiumUrl ? 'pointer' : 'default',
          '&:hover': {
            backgroundColor: 'action.selected',
          },
        }}
      >
        <Avatar
          src={showInitials ? undefined : actorAvatarUrl}
          alt={fromUserName || 'User'}
          imgProps={{ onError: () => setImgBroken(true) }}
          sx={{
            width: 32,
            height: 32,
            backgroundColor: theme.palette.primary.avatar,
            color: 'white',
            cursor: senderProfileUrl ? 'pointer' : 'default',
          }}
          onClick={senderProfileUrl ? handleSenderClick : undefined}
        >
          {showInitials ? fromUserInitials : <GroupAddIcon fontSize="small" />}
        </Avatar>

        <Typography variant="body2" sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          {!isRecipient && senderProfileUrl && fromUserName !== currentUser?.name ? (
            <MuiLink
              component="a"
              href={senderProfileUrl}
              underline="hover"
              color="text.primary"
              fontWeight={600}
              onClick={handleSenderClick}
              sx={{ cursor: 'pointer' }}
            >
              {fromUserName}
            </MuiLink>
          ) : null}
          <Box component="span" color="text.secondary">
            {getMessage()}
          </Box>
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            { formatDistanceToNow(new Date(notification.created), { addSuffix: true }) }
          </Typography>
          {!notification.is_read && (
            <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />
          )}
        </Box>
      </Box>
    );
  }

  // Recipient with pending invitation - show action buttons
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1,
          px: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
          transition: 'background-color 0.2s',
          '&:hover': {
            backgroundColor: 'action.selected',
          },
        }}
      >
        <Avatar
          src={showInitials ? undefined : actorAvatarUrl}
          alt={fromUserName || 'User'}
          imgProps={{ onError: () => setImgBroken(true) }}
          sx={{
            width: 32,
            height: 32,
            backgroundColor: theme.palette.primary.avatar,
            color: 'white',
            cursor: senderProfileUrl ? 'pointer' : 'default',
          }}
          onClick={senderProfileUrl ? handleSenderClick : undefined}
        >
          {showInitials ? fromUserInitials : <GroupAddIcon fontSize="small" />}
        </Avatar>

        <Typography variant="body2" sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          {senderProfileUrl ? (
            <MuiLink
              component="a"
              href={senderProfileUrl}
              underline="hover"
              color="text.primary"
              fontWeight={600}
              onClick={handleSenderClick}
              sx={{ cursor: 'pointer' }}
            >
              {fromUserName}  
            </MuiLink>
          ) : (
            <Box component="span" fontWeight={600}>
              {fromUserName}
            </Box>
          )}
          <Box component="span" color="text.secondary">
            {getMessage()}
          </Box>
        </Typography>

        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            size="small"
            variant="contained"
            color="success"
            onClick={handleAccept}
            disabled={actionLoading}
            sx={{ minWidth: 70 }}
          >
            Accept
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={handleDecline}
            disabled={actionLoading}
            sx={{ minWidth: 70 }}
          >
            Decline
          </Button>
        </Stack>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {formatDistanceToNow(new Date(notification.created), { addSuffix: true })}
          </Typography>
          {!notification.is_read && (
            <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />
          )}
        </Box>
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Cancel</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={actionLoading}>
            Close
          </Button>
          <Button onClick={handleConfirmCancel} color="error" disabled={actionLoading}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default InvitationNotificationCard;